import type React from 'react'
import type { JSX } from 'react'
import type { DropdownRowGap } from './Dropdown.h'

import {
  Children,
  cloneElement,
  isValidElement,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { identifyTouchDevice } from 'utils/helpers'
import { useBooleanState } from 'utils/hooks'
import { DropdownAlignment, DropdownRowGaps } from './Dropdown.h'
import { DropdownBody, DropdownStyled } from './Dropdown.styled'

export interface IDropdownProps {
  children: React.ReactNode
  content?: object[] | boolean
  isOpenedOnHover?: boolean
  rowGap?: DropdownRowGap
  activeId?: number
  width?: string
  alignment?: DropdownAlignment
  className?: string
  style?: React.CSSProperties
  dataTestId?: string
  onClickCapture?: React.MouseEventHandler<HTMLDivElement>
}

export const Dropdown = ({
  children,
  content = true,
  isOpenedOnHover = false,
  rowGap = DropdownRowGaps.SIZE_0,
  activeId,
  alignment = DropdownAlignment.Right,
  className = '', // prop for Linaria
  style, // prop for Linaria
  dataTestId,
  onClickCapture,
}: IDropdownProps): JSX.Element | null => {
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [isOpened, setTrue, setFalse, toggle] = useBooleanState(false)

  useEffect(() => {
    setFalse()
  }, [activeId])

  useEffect(() => {
    const closeOnClickOutsideDropdown = (event: Event): void => {
      if (!dropdownRef?.current?.contains(event.target as Node)) {
        setFalse()
      }
    }

    document.addEventListener('click', closeOnClickOutsideDropdown)
    document.addEventListener('click', closeOnClickOutsideDropdown)
    return () => {
      document.removeEventListener('click', closeOnClickOutsideDropdown)
    }
  }, [dropdownRef])

  const [headerElement, ...bodyElements] = Children.toArray(children) ?? []

  const isTouchDevice = useMemo(identifyTouchDevice, [])

  // Dropdown should not render if content falsy, empty array or array with one item
  return content && (typeof content === 'boolean' || content?.length > 1) ? (
    <DropdownStyled
      ref={dropdownRef}
      className={className}
      style={style}
      onMouseEnter={!isTouchDevice && isOpenedOnHover ? setTrue : undefined}
      onMouseLeave={!isTouchDevice && isOpenedOnHover ? setFalse : undefined}
      onClickCapture={onClickCapture}
      data-testid={dataTestId}
    >
      {isValidElement(headerElement)
        ? cloneElement(headerElement as React.ReactElement<any>, {
            isOpened,
            onClick: toggle,
          })
        : null}
      <DropdownBody
        isOpened={isOpened}
        alignment={alignment}
        rowGap={rowGap}
        onClick={toggle}
      >
        {bodyElements}
      </DropdownBody>
    </DropdownStyled>
  ) : null
}
